import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { ReportResponse } from '../../../domain/report/report-response.model';
import { Utils } from '../../../../../shared/utils/utils';
import { SummedMasterReport } from '../../../domain/report/summed-master-report.model';
import { CsvExportUtils } from '../../../../../shared/utils/csv-export-utils';
import { Publisher } from '../../../../../shared/domain/publisher.model';
import { SortUtils } from '../../../../../shared/utils/sort-utils';
import { RolePermissionService } from '../../../../../shared/auth/rbac/role-permission.service';
import { RolePermission } from '../../../../../shared/auth/rbac/role-permission';
import { KnMainTableDiscrepanciesDialogComponent } from './kn-main-table-discrepancies-dialog.component';
import { KnMainTableMetricsChartDialogComponent } from './kn-main-table-metrics-chart-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { ReportBuilderService } from '../../../../report-builder/report-builder.service';
import { ReportRequest } from '../../../domain/report/report-request.model';
import { ReportFilter } from '../../../domain/report/report-filter.model';
import { ReportMetric } from '../../../domain/report/report-metric.enum';
import { ReportDimension } from '../../../domain/report/report-dimension.enum';
import { BridgeAdUnit } from '../../../../../shared/domain/bridge-ad-unit.model';
import { AdStatsWidgetTypesService } from '../../../../../shared/services/ad-stats-widget-types.service';
import { WidgetType } from '../../../../../shared/domain/widget-type.model';

@Component({
  selector: 'cm-kn-main-table',
  templateUrl: './kn-main-table.component.html',
  animations: [
    trigger('detailExpand', [
      state('void', style({height: '0px', minHeight: '0', visibility: 'hidden'})),
      state('*', style({height: '*', visibility: 'visible'})),
      transition('void <=> *', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  styleUrls: ['./kn-main-table.component.scss'],
  host: {'class': 'cm-kn-main-table'}
})
export class KnMainTableComponent implements OnInit, AfterViewInit {

  @Input()
  paginator: MatPaginator;

  @Input()
  loading = false;

  @Input('visibleColumns')
  visibleColumns = [];

  dataSource = new MatTableDataSource<KnDashboardTableRow>();

  availablePublishers = [];

  public filter = {
    name: null,
    bidder: false,
    videoUnit: false,
    accountManagerEmail: '',
    salesRepresentativeEmail: '',
    includeLowImpsAdUnits: true,
    publisherIds: [],
    adUnitNameFilterCtrl: new FormControl(),
    filterActive: () => this.filter.name || this.filter.bidder ||
      this.filter.videoUnit || (this.filter.publisherIds && this.filter.publisherIds.length),
    reset: () => {
      this.filter.adUnitNameFilterCtrl.setValue(null);
      this.filter.bidder = false;
      this.filter.videoUnit = false;
      this.filter.publisherIds = [];
      this.filterData(false);
    }
  };

  public columns = [
    {key: 'publisher', header: 'Publisher'},
    {key: 'impressions', header: 'Impressions'},
    {key: 'adRequests', header: 'Ad requests', visible: !Utils.isMobile()},
    {key: 'filled', header: 'Filled', visible: !Utils.isMobile()},
    {key: 'refreshRate', header: 'Refresh rate'},
    {key: 'rpm', header: 'RPM', visible: !Utils.isMobile()},
    {key: 'cpmRs', header: 'CPM/RS', visible: !Utils.isMobile()},
    {key: 'inView', header: 'In view', visible: !Utils.isMobile()},
    {key: 'overbid', header: 'BidIncrement (OB)', visible: !Utils.isMobile()},
    {key: 'constantBid', header: 'ConstantBid (CB)', visible: !Utils.isMobile()},
    {key: 'margin', header: 'Margin'},
    {key: 'revenue', header: 'Revenue', visible: !Utils.isMobile()},
    {key: 'netRevenue', header: 'Profit', visible: !Utils.isMobile()},
    {key: 'actions', header: 'Actions'}
  ];

  data: ReportResponse;
  widgetTypesMap: Map<string, WidgetType>;

  constructor(private permissions: RolePermissionService, private matDialog: MatDialog,
              private rbService: ReportBuilderService,
              private widgetTypesService: AdStatsWidgetTypesService,
              private activeRoute: ActivatedRoute) {
    if (!this.permissions.hasPermissions([RolePermission.MAIN_DASH_FINANCE])) {
      this.columns = this.columns.filter(c => c.key !== 'revenue' && c.key !== 'netRevenue');
    }
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    const customFilterFn = (data: KnDashboardTableRow, filter: string) => {
      if (data && data._adUnits) {
        let adUnits = data._adUnits.concat();
        adUnits = adUnits.filter(a => {
          const publisherOk = (!this.filter.publisherIds || !this.filter.publisherIds.length) || this.filter.publisherIds.includes(a.publisherId);
          const impressionOk = this.filter.includeLowImpsAdUnits || a.impressions > 100;
          const bidderOk = !this.filter.bidder || a.bidder;
          const videoOk = !this.filter.videoUnit || a.videoUnit;
          const accountMangerOk = !this.filter.accountManagerEmail || a.accountManagerEmail === this.filter.accountManagerEmail;
          const salesRepresentativeEmailOk = !this.filter.salesRepresentativeEmail || a.salesRepresentativeEmail === this.filter.salesRepresentativeEmail;
          const nameOk = !this.filter.name || a.bridgeAdUnitName.toLowerCase().includes(this.filter.name.toLowerCase());
          return publisherOk && impressionOk && bidderOk && accountMangerOk && salesRepresentativeEmailOk && nameOk && videoOk;
        });
        data._filteredAdUnits = adUnits;
      }
      return !!(data._filteredAdUnits && data._filteredAdUnits.length);
    };
    this.dataSource.filterPredicate = customFilterFn;
  }

  ngOnInit(): void {
    //noinspection TypeScriptUnresolvedFunction
    this.widgetTypesService.getAll({enabled: true}).subscribe(next => {
      this.widgetTypesMap = new Map<string, WidgetType>();
      next.forEach(wt => this.widgetTypesMap.set(wt.name, wt));
    });
    this.filter.adUnitNameFilterCtrl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),)
      .subscribe(next => {
          this.filter.name = next;
          if (next) {
            this.filterData(true);
          } else {
            this.filterData();
          }
        }
      );
  }

  public renderData(data: ReportResponse) {
    if (!data || !data.publishers) {
      return;
    }
    this.data = data;
    this.availablePublishers = [];

    data.publishers.forEach(a => a.netRevenue = a.netRevenue || 0);
    this.dataSource.data = data.publishers ? data.publishers.reduce((prev, curr) => {
      this.availablePublishers.push(Publisher.of(curr.publisherId, curr.publisherName));

      const row = new KnDashboardTableRow();
      data.adUnits.forEach(a => a.netRevenue = a.netRevenue || 0);
      Object.assign(row, curr, {
        _expanded: false,
        _adUnits: data.adUnits
          .filter(a => a.publisherId === curr.publisherId)
          .sort(Utils.sortComparatorByAttribute('netRevenue'))
          .reverse()
      });
      prev.push(row);
      return prev;
    }, [])
      .sort(Utils.sortComparatorByAttribute('netRevenue'))
      .reverse() : [];

    this.availablePublishers.sort(SortUtils.propertyComparatorString('name'));
    this.filterData();
  }

  filterData(expand = false) {
    if (typeof expand != undefined) {
      this.expandPublishers(expand);
    }
    this.dataSource.filter = <string>(this.filter ? this.filter : '');
  }

  areAllPublishersExpanded() {
    return !this.dataSource.data.find(p => !p._expanded);
  }

  expandPublishers(expand) {
    this.dataSource.data.forEach(d => d._expanded = expand);
  }

  exportCsv() {
    const adUnits = this.dataSource.data.reduce((p, c) => {
      return p.concat(c._filteredAdUnits);
    }, []);
    const dataToIncludeInCsv = [];
    adUnits.forEach(unitData => {
      dataToIncludeInCsv.push({
        'Publisher': unitData.publisherName,
        'Ad unit': unitData.bridgeAdUnitName,
        'Impressions': unitData.impressions,
        'Ad Requests': unitData.adRequests,
        'Filled %': unitData.filled,
        'Refresh': unitData.refreshRate,
        'RPM': unitData.rpm,
        'IO CPM': unitData.ioCpm,
        'In view': unitData.inViewTotal,
        'BidIncrement (OB)': unitData.overbid ? unitData.overbid : 0,
        'ConstantBid (CB)': unitData.constantBid ? unitData.constantBid : 0,
        'Revenue': unitData.revenue,
        'Pub. Gross Revenue': unitData.publisherGrossRevenue,
        'Profit': unitData.netRevenue,
        'Ad unit status': unitData.activityStatus,
      });
    });
    dataToIncludeInCsv.sort((a1, a2) => {
      if (a1['Publisher'].toLowerCase() < a2['Publisher'].toLowerCase()) return -1;
      if (a1['Publisher'].toLowerCase() > a2['Publisher'].toLowerCase()) return 1;

      if (a1['Revenue'] > a2['Revenue']) return -1;
      if (a1['Revenue'] < a2['Revenue']) return 1;
      return 0;
    });
    CsvExportUtils.exportCsv(dataToIncludeInCsv, 'dashboard-export');
    return dataToIncludeInCsv;
  }

  openDiscrepanciesDialog(el: any) {
    const data = {
      adUnitId: el.bridgeAdUnitId,
      adUnitName: el.bridgeAdUnitName,
      bidder: el.bidder,
      dateFrom: this.activeRoute.snapshot.queryParams.dateFrom,
      dateTo: this.activeRoute.snapshot.queryParams.dateTo,
    };
    this.matDialog.open(KnMainTableDiscrepanciesDialogComponent, <MatDialogConfig>{
      data: data,
      width: '1200px'
    });
  }

  openMetricsChartDialog(el: any, property: string) {
    const data = {
      adUnitId: el.bridgeAdUnitId,
      property: property
    };
    this.matDialog.open(KnMainTableMetricsChartDialogComponent, <MatDialogConfig>{
      data: data,
      width: '500px',
      panelClass: 'main-table-chart-dialog'
    });
  }

  onUnitClick(id: string, name: string, companionAdUnitId?: string, adUnit?: BridgeAdUnit) {
    if (this.isVideo(adUnit)) {
      const r = new ReportRequest();
      r.filters = new ReportFilter();
      r.filters.adUnitIds = [id];
      try {
        r.filters.dateFrom = this.data.request.filters.dateFrom;
        r.filters.dateTo = this.data.request.filters.dateTo;
      } catch (e) {
      }
      if (companionAdUnitId) {
        r.filters.adUnitIds.push(companionAdUnitId);
      }
      r.dimensions = [
        ReportDimension.AD_UNIT
      ];
      r.metrics = [
        ReportMetric.AD_UNIT,
        ReportMetric.IMPRESSIONS,
        ReportMetric.AD_REQUESTS,
        ReportMetric.FILLED,
        ReportMetric.REFRESH_RATE,
        ReportMetric.RPM,
        ReportMetric.IO_CPM,
        ReportMetric.MARGIN,
        ReportMetric.REVENUE,
        ReportMetric.PUBLISHER_REVENUE,
        ReportMetric.PROFIT,
        ReportMetric.ACCOUNT_REPRESENTATIVE_EMAIL
      ];
      this.rbService.navigateToRbWithConfig(r, true);
    }
  }

  isVideo(adUnit: BridgeAdUnit) {
    if (this.widgetTypesMap) {
      const widgetType = this.widgetTypesMap.get(adUnit.widgetName);
      return widgetType ? widgetType.video : false;
    }
    return false;
  }

}

export class KnDashboardTableRow extends SummedMasterReport {

  _expanded? = true;
  _adUnits?: SummedMasterReport[];
  _filteredAdUnits?: SummedMasterReport[];

  isPublisher() {
    return !!this._adUnits;
  }

}
